import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  congesApi,
  type BaremeEvenement,
  type CategorieAbsence,
  type Mecanisme,
  type RestrictionEligibilite,
  type TypeAbsence,
} from '../../services/conges-api';
import { ndfApi } from '../../services/ndf-api';
import { peutModule } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Etiquette,
  Saisie,
  SaisieNombre,
  Section,
  Selection,
  ValeurCalculee,
  ZoneTexte,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { AideType } from '../../components/conges/AideType';
import {
  brouillonDe,
  brouillonVide,
  corpsTypeAbsence,
  libellePlafond,
  lireMecanismes,
  MECANISME_BAREME,
  MECANISME_PLAFOND,
  type BrouillonType,
} from '../../lib/champs-type-absence';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import { apercuActif } from '../../services/apercu';

/**
 * Paramétrage du module Congés (specs A2, B2, B3).
 *
 * Trois onglets, dans l'ordre où on s'en sert : ce qu'on peut demander, à quoi
 * les congés exceptionnels donnent droit, et les valeurs de calcul.
 */

// L'onglet « Congés exceptionnels » a disparu en v1.9 : sa sous-liste imposait
// au salarié de choisir sa situation pour ouvrir ses droits. Elle est remplacée
// par le BARÈME D'AIDE, qui informe sans contraindre et vit dans la fiche du
// type auquel il se rattache.
type Onglet = 'types' | 'parametres';

const ONGLETS: Array<{ cle: Onglet; libelle: string }> = [
  { cle: 'types', libelle: 'Types d’absence' },
  { cle: 'parametres', libelle: 'Valeurs de calcul' },
];

export function CongesParametragePage() {
  const [onglet, setOnglet] = useState<Onglet>('types');
  const { data: moi, error } = useQuery({
    queryKey: ['ndf', 'moi', apercuActif()],
    queryFn: () => ndfApi.moi(),
    retry: false,
  });

  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <MessageErreur erreur={error} />
      </div>
    );
  }

  if (moi && !peutModule(moi, 'conges', 'administrer')) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-4 text-sm text-jaune">
          Le paramétrage du module Congés est réservé aux administrateurs.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="font-titre text-xl font-bold text-nuit">Paramétrage des congés</h1>
        <p className="text-sm text-nuit/70">
          Ce qui se règle ici décide du comportement du module : les types qu’un salarié peut
          demander, les droits ouverts par chaque événement familial, et les valeurs de calcul.
        </p>
      </header>

      <nav className="mb-4 flex flex-wrap gap-2">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            onClick={() => setOnglet(o.cle)}
            className={`min-h-11 rounded-lg px-3 text-sm font-medium transition-all ${
              onglet === o.cle
                ? 'bg-nuit text-white shadow-sm'
                : 'bg-white text-nuit ring-1 ring-nuit/15 hover:ring-nuit/30 active:translate-y-px'
            }`}
          >
            {o.libelle}
          </button>
        ))}
      </nav>

      {onglet === 'types' && <OngletTypes />}
      {onglet === 'parametres' && (
        <>
          <OngletParametres />
          <SectionAnciennete />
        </>
      )}
    </div>
  );
}

// ─── Types d'absence ────────────────────────────────────────────────────────

const LIBELLE_RESTRICTION: Record<string, string> = {
  aucune: 'Tous les salariés',
  apprentis: 'Contrats d’apprentissage',
  stagiaires: 'Stagiaires',
  forfait_jour: 'Forfait jour',
};

/**
 * Navigation Catégories → Types → Fiche (specs §B2 v1.8, retour de recette).
 *
 * Soixante-dix types à plat seraient illisibles : on choisit une catégorie, puis
 * un type, puis on le consulte. La fiche s'ouvre EN LECTURE — il fallait
 * auparavant entrer en modification pour voir ce qu'un type portait, ce qui
 * revenait à risquer une écriture pour poser une question.
 */
function OngletTypes() {
  const qc = useQueryClient();
  const toast = useToast();
  const [categorieId, setCategorieId] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [edite, setEdite] = useState<BrouillonType | null>(null);

  const { data: cats, isLoading: chargeCats, error: erreurCats } = useQuery({
    queryKey: ['conges', 'categories'],
    queryFn: () => congesApi.categories.liste(),
  });
  const { data: tousTypes, isLoading, error } = useQuery({
    queryKey: ['conges', 'types-absence'],
    queryFn: () => congesApi.typesAbsence.liste(),
  });
  const { data: catalogue } = useQuery({
    queryKey: ['conges', 'mecanismes'],
    queryFn: () => congesApi.mecanismes.liste(),
  });

  const enregistrer = useMutation({
    mutationFn: (b: BrouillonType) => {
      const corps = corpsTypeAbsence(b);
      return b.id
        ? congesApi.typesAbsence.modifier(b.id, corps)
        : congesApi.typesAbsence.creer(corps);
    },
    onSuccess: (r) => {
      toast.success('Type d’absence enregistré.');
      qc.invalidateQueries({ queryKey: ['conges', 'types-absence'] });
      setEdite(null);
      // Après une création, on ouvre la fiche du type créé : sans cela, on
      // retombe sur la liste sans savoir si l'enregistrement a pris.
      setTypeId(r.type.id);
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const categories = cats?.categories ?? [];
  const categorie = categories.find((c) => c.id === categorieId) ?? null;
  const types = (tousTypes?.types ?? []).filter((t) => t.categorie_id === categorieId);
  const type = (tousTypes?.types ?? []).find((t) => t.id === typeId) ?? null;
  const mecanismes = catalogue?.mecanismes ?? [];

  const compter = (id: string) => (tousTypes?.types ?? []).filter((t) => t.categorie_id === id).length;

  if (chargeCats || isLoading) return <p className="p-4 text-sm text-nuit/60">Chargement…</p>;
  if (erreurCats) return <MessageErreur erreur={erreurCats} />;
  if (error) return <MessageErreur erreur={error} />;

  // ── Niveau 3 : la fiche d'un type ──────────────────────────────────────────
  if (type && categorie) {
    return (
      <Section titre={type.libelle} teinte="vert">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <BoutonSecondaire type="button" onClick={() => { setTypeId(null); setEdite(null); }}>
            ‹ {categorie.libelle}
          </BoutonSecondaire>
          <span className="flex items-center gap-2">
            {/* Le « ? » tel que le salarié le verra : le montrer ici permet de
                vérifier ce qu'il dira avant que quiconque le lise. */}
            <AideType type={type} />
            {!edite && (
              <BoutonSecondaire type="button" onClick={() => setEdite(brouillonDe(type))}>
                Modifier
              </BoutonSecondaire>
            )}
          </span>
        </div>

        {edite ? (
          <FormulaireType
            brouillon={edite}
            setBrouillonType={setEdite}
            categories={categories}
            mecanismes={mecanismes}
            enCours={enregistrer.isPending}
            onEnregistrer={() => enregistrer.mutate(edite)}
            onAnnuler={() => setEdite(null)}
          />
        ) : (
          <FicheTypeEnLecture type={type} categorie={categorie} mecanismes={mecanismes} />
        )}

        {lireMecanismes(type.mecanismes).includes('bareme_aide') && !edite && (
          <SectionBareme type={type} />
        )}
      </Section>
    );
  }

  // ── Niveau 1 : les catégories ──────────────────────────────────────────────
  if (!categorie) {
    return (
      <Section titre="Types d’absence" teinte="vert">
        <p className="mb-3 text-sm text-nuit/70">
          Les sept catégories viennent du référentiel de la paie : elles ne se créent ni ne se
          suppriment. Choisissez-en une pour voir et régler ses types.
        </p>
        <ul className="space-y-2">
          {categories.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setCategorieId(c.id)}
                className="flex w-full items-center justify-between gap-3 rounded-md border border-nuit/15 p-3 text-left hover:border-nuit/30 hover:bg-nuit/5"
              >
                <span className="font-medium text-nuit">{c.libelle}</span>
                <span className="text-sm text-nuit/60">
                  {compter(c.id)} type{compter(c.id) > 1 ? 's' : ''} ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Section>
    );
  }

  // ── Niveau 2 : les types de la catégorie ───────────────────────────────────
  return (
    <Section titre={`Types d’absence, ${categorie.libelle}`} teinte="vert">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <BoutonSecondaire type="button" onClick={() => { setCategorieId(null); setEdite(null); }}>
          ‹ Toutes les catégories
        </BoutonSecondaire>
        {!edite && (
          <BoutonSecondaire
            type="button"
            onClick={() => setEdite(brouillonVide(categorie.id, (types.at(-1)?.ordre ?? 0) + 10))}
          >
            Ajouter un type
          </BoutonSecondaire>
        )}
      </div>

      {edite && !edite.id && (
        <div className="mb-3 rounded-md border border-nuit/15 p-3">
          <p className="mb-2 font-medium text-nuit">Nouveau type</p>
          <FormulaireType
            brouillon={edite}
            setBrouillonType={setEdite}
            categories={categories}
            mecanismes={mecanismes}
            enCours={enregistrer.isPending}
            onEnregistrer={() => enregistrer.mutate(edite)}
            onAnnuler={() => setEdite(null)}
          />
        </div>
      )}

      <ul className="space-y-2">
        {types.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => setTypeId(t.id)}
              className="flex w-full items-center justify-between gap-3 rounded-md border border-nuit/15 p-3 text-left hover:border-nuit/30 hover:bg-nuit/5"
            >
              <span className="min-w-0">
                <span className="block font-medium text-nuit">{t.libelle}</span>
                <span className="mt-1 flex flex-wrap gap-1">
                  <Etiquette>
                    {t.origine === 'rh' ? 'Saisi par la RH' : 'Demandé par le salarié'}
                  </Etiquette>
                  {t.visible_collaborateur && <Etiquette ton="actif">Visible</Etiquette>}
                  {!t.actif && <Etiquette>Désactivé</Etiquette>}
                </span>
              </span>
              <span className="text-sm text-nuit/60">›</span>
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** La fiche en LECTURE : ce que le type porte, sans risquer d'y toucher. */
function FicheTypeEnLecture({
  type,
  categorie,
  mecanismes,
}: {
  type: TypeAbsence;
  categorie: CategorieAbsence;
  mecanismes: Mecanisme[];
}) {
  const actifs = lireMecanismes(type.mecanismes);
  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <Champ label="Catégorie">
          <ValeurCalculee>{categorie.libelle}</ValeurCalculee>
        </Champ>
        <Champ label="Code technique" aide="Écrit dans les demandes et la paie : non modifiable.">
          <ValeurCalculee>{type.code}</ValeurCalculee>
        </Champ>
        <Champ label="Qui le saisit ?">
          <ValeurCalculee>
            {type.origine === 'rh' ? 'La RH le saisit' : 'Le salarié le demande'}
          </ValeurCalculee>
        </Champ>
        <Champ label="Visible du collaborateur">
          <ValeurCalculee>{type.visible_collaborateur ? 'Oui' : 'Non'}</ValeurCalculee>
        </Champ>
        <Champ label="Réservé à">
          <ValeurCalculee>{LIBELLE_RESTRICTION[type.restriction_eligibilite]}</ValeurCalculee>
        </Champ>
        {lireMecanismes(type.mecanismes).includes(MECANISME_PLAFOND) && (
          <Champ label="Plafond">
            <ValeurCalculee>{libellePlafond(type) ?? ''}</ValeurCalculee>
          </Champ>
        )}
        <Champ label="Code de paie (Silae)">
          <ValeurCalculee>{type.code_silae ?? ''}</ValeurCalculee>
        </Champ>
        <Champ label="Ordre d’affichage">
          <ValeurCalculee>{String(type.ordre)}</ValeurCalculee>
        </Champ>
      </div>

      {type.bareme_intro && lireMecanismes(type.mecanismes).includes(MECANISME_BAREME) && (
        <Champ label="Phrase d’introduction du barème">
          <ValeurCalculee>{type.bareme_intro}</ValeurCalculee>
        </Champ>
      )}
      {type.aide_plafond && lireMecanismes(type.mecanismes).includes(MECANISME_PLAFOND) && (
        <Champ label="Précision affichée au salarié">
          <ValeurCalculee>{type.aide_plafond}</ValeurCalculee>
        </Champ>
      )}

      <div>
        <p className="mb-1 text-sm font-medium text-nuit">Mécanismes</p>
        {actifs.length === 0 ? (
          <p className="text-sm text-nuit/50">Aucun, une absence simple.</p>
        ) : (
          <ul className="space-y-1">
            {actifs.map((code) => {
              const m = mecanismes.find((x) => x.code === code);
              return (
                <li key={code} className="text-sm text-nuit">
                  <span className="font-medium">{m?.libelle ?? code}</span>
                  {m?.aide && <span className="block text-xs text-nuit/60">{m.aide}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!type.actif && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-2 text-sm text-jaune">
          Type désactivé : il n’apparaît plus dans les formulaires, mais reste lisible dans
          l’historique des demandes qui le portent.
        </p>
      )}
    </div>
  );
}

/**
 * Le barème d'aide d'un type (specs §B3 v1.9).
 *
 * Il traduit un accord d'entreprise : il se modifie sans redéploiement, parce
 * que les accords changent plus souvent que le code. Et il n'engage AUCUN
 * contrôle automatique — c'est le circuit à deux niveaux qui arbitre.
 */
function SectionBareme({ type }: { type: TypeAbsence }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [edite, setEdite] = useState<Partial<BaremeEvenement> | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'baremes', type.id],
    queryFn: () => congesApi.baremes.liste(type.id),
  });

  const enregistrer = useMutation({
    mutationFn: (b: Partial<BaremeEvenement>) =>
      b.id
        ? congesApi.baremes.modifier(b.id, {
            libelle: b.libelle,
            nb_jours: b.nb_jours,
            precisions: b.precisions || null,
            actif: b.actif,
          })
        : congesApi.baremes.creer({
            type_absence_id: type.id,
            libelle: b.libelle,
            nb_jours: b.nb_jours,
            precisions: b.precisions || null,
          }),
    onSuccess: () => {
      toast.success('Barème enregistré.');
      qc.invalidateQueries({ queryKey: ['conges', 'baremes', type.id] });
      setEdite(null);
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const lignes = data?.baremes ?? [];

  return (
    <div className="mt-4 border-t border-nuit/10 pt-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-titre font-bold text-nuit">Barème d’aide</p>
          <p className="text-xs text-nuit/60">
            Affiché au salarié et aux valideurs via le « ? ». <strong>Informatif</strong> : aucun
            contrôle automatique n’en découle, le nombre de jours reste arbitré par le circuit.
          </p>
        </div>
        {!edite && (
          <BoutonSecondaire type="button" onClick={() => setEdite({ nb_jours: 1 })}>
            Ajouter une situation
          </BoutonSecondaire>
        )}
      </div>

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <ul className="space-y-1">
        {lignes.map((b) => (
          <li
            key={b.id}
            className={`flex flex-wrap items-start justify-between gap-2 rounded-md border p-2 ${
              b.actif ? 'border-nuit/15' : 'border-dashed border-nuit/15 opacity-60'
            }`}
          >
            <span className="min-w-0 text-sm">
              <strong className="text-nuit">
                {b.nb_jours} jour{b.nb_jours > 1 ? 's' : ''}
              </strong>{' '}
              <span className="text-nuit/80">{b.libelle}</span>
              {b.precisions && <span className="block text-xs text-nuit/60">{b.precisions}</span>}
            </span>
            {!edite && (
              <BoutonSecondaire type="button" onClick={() => setEdite(b)}>
                Modifier
              </BoutonSecondaire>
            )}
          </li>
        ))}
      </ul>

      {edite && (
        <div className="mt-2 space-y-3 rounded-md border border-nuit/15 p-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <Champ label="Situation" requis>
              <Saisie
                value={edite.libelle ?? ''}
                onChange={(e) => setEdite({ ...edite, libelle: e.target.value })}
                placeholder="Obsèques du père ou de la mère"
              />
            </Champ>
            <Champ label="Jours ouvrés" requis>
              <SaisieNombre
                nullable
                valeur={edite.nb_jours ?? null}
                onValeur={(v) => setEdite({ ...edite, nb_jours: v ?? undefined })}
                inputMode="numeric"
              />
            </Champ>
          </div>
          <Champ label="Précisions" aide="Ce que le nombre de jours ne dit pas à lui seul.">
            <ZoneTexte
              value={edite.precisions ?? ''}
              onChange={(e) => setEdite({ ...edite, precisions: e.target.value })}
            />
          </Champ>
          {edite.id && (
            <Case
              label="Actif"
              checked={edite.actif !== false}
              onChange={(e) => setEdite({ ...edite, actif: e.target.checked })}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal
              type="button"
              disabled={enregistrer.isPending}
              onClick={() => enregistrer.mutate(edite)}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
            <BoutonSecondaire type="button" onClick={() => setEdite(null)}>
              Annuler
            </BoutonSecondaire>
          </div>
        </div>
      )}
    </div>
  );
}


/** Le formulaire d'un type — le même pour la création et la modification. */
function FormulaireType({
  brouillon,
  setBrouillonType,
  categories,
  mecanismes,
  enCours,
  onEnregistrer,
  onAnnuler,
}: {
  brouillon: BrouillonType;
  setBrouillonType: (b: BrouillonType) => void;
  categories: CategorieAbsence[];
  mecanismes: Mecanisme[];
  enCours: boolean;
  onEnregistrer: () => void;
  onAnnuler: () => void;
}) {
  const avecPlafond = brouillon.mecanismes.includes(MECANISME_PLAFOND);

  const basculer = (code: string, actif: boolean) =>
    setBrouillonType({
      ...brouillon,
      mecanismes: actif
        ? [...brouillon.mecanismes, code]
        : brouillon.mecanismes.filter((m) => m !== code),
    });

  return (
    <div className="mt-3 space-y-3 border-t border-nuit/10 pt-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ label="Libellé" requis>
          <Saisie
            value={brouillon.libelle}
            onChange={(e) => setBrouillonType({ ...brouillon, libelle: e.target.value })}
          />
        </Champ>
        <Champ label="Catégorie" requis aide="Elle décide de ce que le type est.">
          <Selection
            value={brouillon.categorie_id}
            onChange={(e) => setBrouillonType({ ...brouillon, categorie_id: e.target.value })}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Qui le saisit ?" requis>
          <Selection
            value={brouillon.origine}
            onChange={(e) =>
              setBrouillonType({ ...brouillon, origine: e.target.value as 'collaborateur' | 'rh' })
            }
          >
            <option value="collaborateur">Le salarié le demande</option>
            <option value="rh">La RH le saisit</option>
          </Selection>
        </Champ>
        <Champ
          label="Réservé à"
          aide="Le type n’est proposé qu’aux salariés qui remplissent cette condition."
        >
          <Selection
            value={brouillon.restriction_eligibilite}
            onChange={(e) =>
              setBrouillonType({
                ...brouillon,
                restriction_eligibilite: e.target.value as RestrictionEligibilite,
              })
            }
          >
            {Object.entries(LIBELLE_RESTRICTION).map(([code, libelle]) => (
              <option key={code} value={code}>
                {libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Ordre d’affichage">
          <SaisieNombre
            nullable
            valeur={brouillon.ordre}
            onValeur={(v) => setBrouillonType({ ...brouillon, ordre: v ?? 0 })}
            inputMode="numeric"
          />
        </Champ>
        <Champ
          label="Plafond annuel (jours)"
          aide="Utilisé par le « compteur annuel simple ». Le dépassement alerte le valideur, il ne bloque pas."
        >
          <SaisieNombre
            nullable
            valeur={brouillon.plafond_annuel}
            onValeur={(v) => setBrouillonType({ ...brouillon, plafond_annuel: v })}
            inputMode="numeric"
          />
        </Champ>
        <Champ
          label="Code de paie (Silae)"
          aide="Fourni par KPMG. Vide, l’absence ne partira pas en paie automatiquement."
        >
          <Saisie
            value={brouillon.code_silae}
            onChange={(e) => setBrouillonType({ ...brouillon, code_silae: e.target.value })}
          />
        </Champ>
      </div>

      {brouillon.mecanismes.includes(MECANISME_BAREME) && (
        <Champ
          label="Phrase d’introduction du barème"
          aide="Elle annonce les situations dans le « ? », et cite la source du droit."
        >
          <ZoneTexte
            value={brouillon.bareme_intro}
            onChange={(e) => setBrouillonType({ ...brouillon, bareme_intro: e.target.value })}
            placeholder="Durées prévues par la convention collective Syntec"
          />
        </Champ>
      )}

      <Champ
        label="Précision affichée dans le « ? »"
        aide="Ce qu'aucun compteur ne sait tenir : une fenêtre de prise, une extension légale."
      >
        <ZoneTexte
          value={brouillon.aide_plafond}
          onChange={(e) => setBrouillonType({ ...brouillon, aide_plafond: e.target.value })}
          placeholder="À prendre dans une fenêtre de 15 jours entourant la naissance."
        />
      </Champ>

      <Case
        label="Visible du collaborateur"
        aide="Décoché, le type n’apparaît que côté RH : c’est le cas de la plupart."
        checked={brouillon.visible_collaborateur}
        onChange={(e) => setBrouillonType({ ...brouillon, visible_collaborateur: e.target.checked })}
      />
      <Case
        label="Actif"
        aide="Désactivé, le type disparaît des formulaires mais reste lisible dans l’historique."
        checked={brouillon.actif}
        onChange={(e) => setBrouillonType({ ...brouillon, actif: e.target.checked })}
      />

      <div>
        <p className="mb-1 text-sm font-medium text-nuit">Mécanismes</p>
        <p className="mb-2 text-xs text-nuit/60">
          Ce que le type déclenche. Ils sont fournis par l’application : en ajouter un nouveau
          demande un développement.
        </p>
        <div className="grid gap-1 sm:grid-cols-2">
          {mecanismes.map((m) => (
            <Case
              key={m.code}
              label={m.libelle}
              aide={m.aide}
              checked={brouillon.mecanismes.includes(m.code)}
              onChange={(e) => basculer(m.code, e.target.checked)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <BoutonPrincipal type="button" disabled={enCours} onClick={onEnregistrer}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
        <BoutonSecondaire type="button" onClick={onAnnuler}>
          Annuler
        </BoutonSecondaire>
      </div>
    </div>
  );
}


// ─── Valeurs de calcul ──────────────────────────────────────────────────────

/**
 * L'ordre de la grille, ligne par ligne : deux clés par ligne, celles qui se
 * lisent ensemble face à face.
 */
/** Les réglages qui contiennent une phrase ou un paragraphe, pas une valeur. */
const PARAMETRES_LONGS = ['conges_mention_conge_principal'];

const ORDRE_PARAMETRES = [
  'conges_acquisition_cp_par_mois',
  'conges_forfait_jour',
  'conges_absence_autorisee_par_mois',
  'conges_examens_par_an',
  'conges_methode_proratisation_rtt',
  'conges_premier_mois_tickets',
  'conges_pause_dejeuner_debut',
  'conges_pause_dejeuner_fin',
  'conges_lundi_pentecote_chome',
  'conges_mention_conge_principal',
];

function ordonner<T extends { key: string }>(liste: T[]): T[] {
  const rang = (k: string) => {
    const i = ORDRE_PARAMETRES.indexOf(k);
    return i === -1 ? ORDRE_PARAMETRES.length : i;
  };
  return [...liste].sort((a, b) => rang(a.key) - rang(b.key) || a.key.localeCompare(b.key));
}

/** Le nom du réglage, en français — « conges_forfait_jour » ne se lit pas. */
const LIBELLE_PARAMETRE: Record<string, string> = {
  conges_acquisition_cp_par_mois: 'Acquisition mensuelle de congés payés',
  conges_forfait_jour: 'Forfait jour (base RTT)',
  conges_pause_dejeuner_debut: 'Pause déjeuner (début)',
  conges_pause_dejeuner_fin: 'Pause déjeuner (fin)',
  conges_absence_autorisee_par_mois: 'Absence autorisée par mois de stage',
  conges_examens_par_an: 'Congé Examens par an',
  conges_lundi_pentecote_chome: 'Lundi de Pentecôte chômé',
  conges_methode_proratisation_rtt: 'Proratisation des RTT sur absence',
  conges_premier_mois_tickets: 'Premier mois de calcul des tickets restaurant',
  conges_mention_conge_principal: 'Mention légale « congé principal »',
};

/** Ce que chaque clé veut dire, en français — la clé technique ne suffit pas. */
const AIDE_PARAMETRE: Record<string, string> = {
  conges_acquisition_cp_par_mois: 'Jours de congés payés acquis chaque mois.',
  conges_forfait_jour: 'Base du calcul des RTT : 217 jours + 1 de solidarité.',
  conges_pause_dejeuner_debut: 'Début de la pause déjeuner théorique (droit au ticket restaurant).',
  conges_pause_dejeuner_fin: 'Fin de la pause déjeuner théorique.',
  conges_absence_autorisee_par_mois: 'Jours d’absence autorisée par mois de stage.',
  conges_examens_par_an: 'Jours de congé Examens par année civile, pour les apprentis.',
  conges_lundi_pentecote_chome: '« true » : le Lundi de Pentecôte est chômé et compte comme férié.',
  conges_methode_proratisation_rtt:
    '« aucune » sous l’accord d’entreprise actuel : un arrêt de travail ne réduit pas les RTT. « prorata » n’est à activer qu’en cas de nouvel accord.',
  conges_mention_conge_principal:
    'Affichée en tête du formulaire de demande, pour les congés payés uniquement (R-ABS-02).',
  conges_premier_mois_tickets:
    'Au format AAAA-MM. Les mois antérieurs ont été traités dans Airtable : les recalculer paierait deux fois la même période. Vide : aucun ticket n’est calculé.',
};

/**
 * Le barème des congés d'ancienneté Syntec (R-CPT-13).
 *
 * Administrable, parce que la convention évolue : un barème recopié dans le
 * code se périme sans que personne ne s'en aperçoive.
 *
 * La SIMULATION à côté n'est pas un ornement — le crédit tombe une fois l'an, en
 * décembre, sur le compteur de congés payés. Sans elle, une erreur de barème ne
 * se découvrirait qu'en janvier, sur des soldes déjà faux.
 */
function SectionAnciennete() {
  const qc = useQueryClient();
  const toast = useToast();
  const [edition, setEdition] = useState(false);
  const [paliers, setPaliers] = useState<Array<{ annees: number | null; jours: number | null }>>([]);
  const annee = new Date().getFullYear();

  const bareme = useQuery({
    queryKey: ['conges', 'bareme-anciennete'],
    queryFn: () => congesApi.anciennete.bareme(),
  });
  const simulation = useQuery({
    queryKey: ['conges', 'anciennete-simulation', annee],
    queryFn: () => congesApi.anciennete.simulation(annee),
  });

  const ouvrirEdition = () => {
    setPaliers((bareme.data?.paliers ?? []).map((p) => ({ annees: p.annees, jours: p.jours })));
    setEdition(true);
  };

  const enregistrer = useMutation({
    mutationFn: () =>
      congesApi.anciennete.enregistrerBareme(
        paliers
          .filter((p) => p.annees !== null && p.jours !== null)
          .map((p) => ({ annees: p.annees as number, jours: p.jours as number })),
      ),
    onSuccess: () => {
      toast.success('Barème d’ancienneté enregistré.');
      setEdition(false);
      qc.invalidateQueries({ queryKey: ['conges', 'bareme-anciennete'] });
      qc.invalidateQueries({ queryKey: ['conges', 'anciennete-simulation'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const lignes = simulation.data?.lignes ?? [];
  const concernes = lignes.filter((l) => l.jours > 0);

  return (
    <Section
      titre="Congés d’ancienneté"
      teinte="violet"
      description="Barème Syntec. Le salarié touche le palier le plus élevé qu’il a atteint, jamais la somme des paliers : dix-huit ans d’ancienneté valent trois jours, pas six. Le crédit tombe automatiquement en décembre, sur les congés payés de l’année."
      actions={
        !edition ? (
          <BoutonSecondaire type="button" onClick={ouvrirEdition}>
            Modifier
          </BoutonSecondaire>
        ) : (
          <div className="flex gap-2">
            <BoutonSecondaire type="button" onClick={() => setEdition(false)}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              disabled={enregistrer.isPending}
              onClick={() => enregistrer.mutate()}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </div>
        )
      }
    >
      {bareme.error && <MessageErreur erreur={bareme.error} />}

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-nuit">Barème</h3>
          {!edition ? (
            <ul className="space-y-1 text-sm text-nuit/80">
              {(bareme.data?.paliers ?? []).map((p) => (
                <li key={p.annees}>
                  À partir de <strong>{p.annees} ans</strong>, {p.jours} jour{p.jours > 1 ? 's' : ''}
                </li>
              ))}
              {(bareme.data?.paliers ?? []).length === 0 && (
                <li className="text-nuit/60">Aucun palier : aucun congé d’ancienneté n’est accordé.</li>
              )}
            </ul>
          ) : (
            <div className="space-y-2">
              {paliers.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-sm text-nuit/70">À partir de</span>
                  <SaisieNombre
                    nullable
                    valeur={p.annees}
                    onValeur={(v) =>
                      setPaliers((l) => l.map((x, j) => (j === i ? { ...x, annees: v } : x)))
                    }
                    className="w-20"
                    inputMode="numeric"
                  />
                  <span className="text-sm text-nuit/70">ans →</span>
                  <SaisieNombre
                    nullable
                    valeur={p.jours}
                    onValeur={(v) =>
                      setPaliers((l) => l.map((x, j) => (j === i ? { ...x, jours: v } : x)))
                    }
                    className="w-20"
                    inputMode="numeric"
                  />
                  <span className="text-sm text-nuit/70">j</span>
                  <BoutonSecondaire
                    type="button"
                    className="!min-h-9 !px-2"
                    onClick={() => setPaliers((l) => l.filter((_, j) => j !== i))}
                  >
                    Retirer
                  </BoutonSecondaire>
                </div>
              ))}
              <BoutonSecondaire
                type="button"
                onClick={() => setPaliers((l) => [...l, { annees: null, jours: null }])}
              >
                Ajouter un palier
              </BoutonSecondaire>
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-nuit">
            Ce que le crédit de décembre {annee} donnerait
          </h3>
          {simulation.isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
          {concernes.length === 0 && !simulation.isLoading ? (
            <p className="text-sm text-nuit/60">Personne n’atteint le premier palier cette année.</p>
          ) : (
            <ul className="space-y-1 text-sm text-nuit/80">
              {concernes.map((l) => (
                <li key={l.salarie_id} className="flex justify-between gap-3">
                  <span>
                    {l.nom} <span className="text-nuit/50">({l.annees} ans)</span>
                  </span>
                  <span className="tabular-nums">
                    +{l.jours} j
                    {l.deja_credite && <span className="ml-2 text-vert">déjà crédité</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-nuit/60">
            Rien n’est écrit ici : la simulation lit les fiches et le barème. Le crédit réel est posé
            par la tâche de fond, en décembre, et ne peut pas tomber deux fois.
          </p>
        </div>
      </div>
    </Section>
  );
}

function OngletParametres() {
  const qc = useQueryClient();
  const toast = useToast();
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillonType] = useState<Record<string, string>>({});
  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'parametres'],
    queryFn: () => congesApi.parametres.liste(),
  });
  // L'ordre d'affichage est CHOISI, pas celui de la base : la grille est à deux
  // colonnes, et les réglages qui se lisent ensemble doivent tomber en face
  // l'un de l'autre. Les clés inconnues suivent, dans leur ordre d'arrivée.
  const parametres = ordonner(data?.parametres ?? []);

  // Un seul enregistrement pour toute la grille : ces valeurs se règlent
  // ensemble, et un bouton par champ multiplierait les gestes sans rien
  // apporter.
  const enregistrer = useMutation({
    mutationFn: async (modifs: Record<string, string>) => {
      for (const [cle, valeur] of Object.entries(modifs)) {
        await congesApi.parametres.enregistrer(cle, valeur);
      }
      return Object.keys(modifs).length;
    },
    onSuccess: (n) => {
      toast.success(n === 0 ? 'Aucune modification.' : `${n} paramètre(s) enregistré(s).`);
      setEdition(false);
      setBrouillonType({});
      qc.invalidateQueries({ queryKey: ['conges', 'parametres'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  return (
    <Section
      titre="Valeurs de calcul"
      teinte="corail"
      description="Modifiables sans redéploiement : aucune n’est écrite dans le code."
    >
      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <div className="grid gap-4 sm:grid-cols-2">
        {parametres.map((p) => (
          // Un paragraphe occupe la largeur : le laisser dans une demi-colonne
          // rendrait la zone multiligne aussi étroite que le champ qu'elle
          // remplace.
          <div key={p.key} className={PARAMETRES_LONGS.includes(p.key) ? 'sm:col-span-2' : ''}>
          <Champ
            label={LIBELLE_PARAMETRE[p.key] ?? p.key.replace(/^conges_/, '').replace(/_/g, ' ')}
            aide={AIDE_PARAMETRE[p.key]}
          >
            {edition ? (
              // Un paragraphe s'édite dans une zone multiligne : sur une seule
              // ligne, on corrige un texte juridique par une fenêtre de
              // quarante caractères, sans jamais le relire en entier.
              PARAMETRES_LONGS.includes(p.key) ? (
                <ZoneTexte
                  value={brouillon[p.key] ?? p.value ?? ''}
                  onChange={(e) => setBrouillonType((b) => ({ ...b, [p.key]: e.target.value }))}
                  rows={7}
                />
              ) : (
                <Saisie
                  value={brouillon[p.key] ?? p.value ?? ''}
                  onChange={(e) => setBrouillonType((b) => ({ ...b, [p.key]: e.target.value }))}
                />
              )
            ) : (
              <ValeurCalculee>{p.value ?? ''}</ValeurCalculee>
            )}
          </Champ>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        {!edition ? (
          <BoutonSecondaire type="button" onClick={() => setEdition(true)}>
            Modifier
          </BoutonSecondaire>
        ) : (
          <>
            <BoutonSecondaire
              type="button"
              onClick={() => {
                setEdition(false);
                setBrouillonType({});
              }}
            >
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              disabled={enregistrer.isPending}
              onClick={() => {
                // On n'envoie que ce qui a réellement changé.
                const modifs = Object.fromEntries(
                  Object.entries(brouillon).filter(
                    ([cle, v]) => v !== (parametres.find((p) => p.key === cle)?.value ?? ''),
                  ),
                );
                enregistrer.mutate(modifs);
              }}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </>
        )}
      </div>
    </Section>
  );
}
